import React from "react";
import { useSelector } from "react-redux";
import type { All8PageContent } from "@bcc/core-renderers";
import type { ContentFieldValueFieldConfig, Path } from "../../Pages/pageComponentLibrary";
import type { IApplicationState } from "../../Redux/Reducers";
import { getValueAtPath } from "./pageEditorUtils";

type ContentFieldValueControlProps = {
	editable: boolean;
	field: ContentFieldValueFieldConfig;
	componentIndex: number;
	fullPath: Path;
	parentPath: Path;
	previewComponents: All8PageContent;
	updateComponentValue: (index: number, path: Path, value: any) => void;
	value: any;
};

const normalizeFieldType = (raw?: string) => (raw ?? "").trim().toLowerCase();

const normalizeVariableToken = (raw?: string) => {
	const value = (raw ?? "").toString().trim();
	if (value.startsWith("{") && value.endsWith("}")) {
		return value.slice(1, -1).trim();
	}
	return value;
};

const ContentFieldValueControl: React.FC<ContentFieldValueControlProps> = ({
	editable,
	field,
	componentIndex,
	fullPath,
	parentPath,
	previewComponents,
	updateComponentValue,
	value
}) => {
	const contentTypeConfigs = useSelector((state: IApplicationState) => state.contentTypeConfigs.items);

	const resolveContentType = (contentTypeKey?: string | null) => {
		if (!contentTypeKey) return null;
		const normalized = contentTypeKey.toString().trim().toLowerCase();
		if (!normalized) return null;
		return contentTypeConfigs.find((config) => (config.id ?? "").toString().trim().toLowerCase() === normalized) ?? null;
	};

	const configField = field;
	const contentTypeKey = getValueAtPath(previewComponents[componentIndex], configField.contentTypePath);
	const config = resolveContentType((contentTypeKey ?? "").toString());
	const fieldKeyRaw = getValueAtPath(previewComponents[componentIndex], [...parentPath, ...configField.fieldPath]);
	const fieldKey = normalizeVariableToken(fieldKeyRaw as string);
	const resolvedField = (config?.fields ?? []).find(
		(f) => (f.key ?? f.title ?? "").trim().toLowerCase() === fieldKey.trim().toLowerCase()
	);
	const fieldType = normalizeFieldType(resolvedField?.type);
	const isBoolean = fieldType === "bool" || fieldType === "boolean";
	const isNumber = ["number", "int", "float", "double", "decimal"].includes(fieldType);
	const isDate = fieldType === "date" || fieldType === "datetime";

	return (
		<div className="space-y-1">
			<label className="text-xs font-semibold text-slate-600">{field.label}</label>
			{isBoolean ? (
				<select
					value={value === true ? "true" : value === false ? "false" : ""}
					onChange={(e) => {
						const raw = e.target.value;
						const next = raw === "" ? "" : raw === "true";
						updateComponentValue(componentIndex, fullPath, next);
					}}
					disabled={!editable}
					className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
				>
					<option value="">Select</option>
					<option value="true">True</option>
					<option value="false">False</option>
				</select>
			) : isNumber ? (
				<input
					type="number"
					value={value ?? ""}
					onChange={(e) => {
						const raw = e.target.value;
						const nextValue = raw === "" ? "" : Number(raw);
						updateComponentValue(componentIndex, fullPath, Number.isNaN(nextValue) ? "" : nextValue);
					}}
					disabled={!editable}
					className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
				/>
			) : isDate ? (
				<input
					type="date"
					value={value ?? ""}
					onChange={(e) => updateComponentValue(componentIndex, fullPath, e.target.value)}
					disabled={!editable}
					className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
				/>
			) : (
				<input
					type="text"
					value={(value ?? "") as string}
					onChange={(e) => updateComponentValue(componentIndex, fullPath, e.target.value)}
					disabled={!editable}
					className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
				/>
			)}
			{field.helper && <p className="text-[11px] text-slate-500">{field.helper}</p>}
		</div>
	);
};

export default ContentFieldValueControl;
